import React, { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { format } from "date-fns";
import { CalendarIcon, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import {
  phonePrefixes,
  countriesList,
  productDropdown,
  incotermsOptions,
  paymentTermsOptions,
} from "../mock";

export default function QuoteForm({ compact = false, simple = false, specPlaceholder }) {
  const [form, setForm] = useState({
    title: "Mr.",
    fullName: "",
    designation: "",
    company: "",
    email: "",
    prefix: "+971",
    phone: "",
    product: "",
    productSpec: "",
    quantity: "",
    originCountry: "",
    destPort: "",
    destCountry: "",
    incoterms: "",
    paymentTerms: "",
    message: "",
  });
  const [placementDate, setPlacementDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.phone || !form.company) {
      toast.error("Please fill in all required fields (Name, Company, Email, Phone).");
      return;
    }
    if (!simple && !form.product) {
      toast.error("Please select a product from the dropdown.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.fullName,
        email: form.email,
        phone: `${form.prefix || ""} ${form.phone}`.trim(),
        company: form.company,
        country: form.destCountry || form.originCountry || "",
        product: form.product,
        quantity: form.quantity,
        incoterm: form.incoterms,
        port: form.destPort,
        spec: form.productSpec,
        message: form.message || `Payment terms: ${form.paymentTerms || "N/A"} | Placement date: ${placementDate || "N/A"}`,
      };
      await api.post("/inquiries/quote", payload);
      setDone(true);
      toast.success("Your query has been received. Our trade team will contact you within one business day.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="bg-white rounded-xl border border-border p-8 text-center">
        <CheckCircle2 className="mx-auto mb-3" size={48} style={{ color: "var(--brand-500)" }} />
        <h3 className="font-display font-bold text-2xl mb-2" style={{ color: "var(--brand-700)" }}>Thank you!</h3>
        <p className="text-muted-foreground">Your inquiry has been received. Our trade team will reach out within one business day.</p>
        <Button onClick={() => { setDone(false); setForm({ ...form, fullName: "", company: "", email: "", phone: "", product: "", productSpec: "", quantity: "", message: "" }); }} className="mt-5 text-white" style={{ backgroundColor: "var(--brand-700)" }}>
          Submit another
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`bg-white rounded-xl border border-border ${compact ? "p-6" : "p-7 md:p-8"} shadow-sm`}>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title *</Label>
          <Select value={form.title} onValueChange={set("title")}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Mr.">Mr.</SelectItem>
              <SelectItem value="Ms.">Ms.</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full Name *</Label>
          <Input className="mt-1.5" value={form.fullName} onChange={set("fullName")} placeholder="John Doe" required />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Company / Organization Name *</Label>
          <Input className="mt-1.5" value={form.company} onChange={set("company")} placeholder="ACME Cement Industries Ltd." required />
        </div>
        {!simple && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Designation</Label>
            <Input className="mt-1.5" value={form.designation} onChange={set("designation")} placeholder="Procurement Manager" />
          </div>
        )}
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email *</Label>
          <Input className="mt-1.5" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" required />
        </div>
        <div className={simple ? "md:col-span-1" : ""}>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone *</Label>
          <div className="mt-1.5 flex gap-2">
            <Select value={form.prefix} onValueChange={set("prefix")}>
              <SelectTrigger className="w-[105px]"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {phonePrefixes.map((p) => (
                  <SelectItem key={p.code} value={p.code}>{p.code} {p.country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="flex-1"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d\s\-()]/g, "") })}
              pattern="[0-9\s\-()]{6,}"
              placeholder="56 389 8675"
              required
            />
          </div>
        </div>
        {!simple && (
          <>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Product Inquiry *</Label>
              <Select value={form.product} onValueChange={set("product")}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select a product" /></SelectTrigger>
                <SelectContent>
                  {productDropdown.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Custom Sizing / Specifications</Label>
              <Textarea
                rows={3}
                className="mt-1.5"
                value={form.productSpec}
                onChange={set("productSpec")}
                placeholder={specPlaceholder || "e.g. Grey Clinker, size 25–40mm, C3S ≥ 58, moisture nil"}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Order Quantity (Tons)</Label>
              <Input className="mt-1.5" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 50,000" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Order Placement Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="mt-1.5 w-full justify-start text-left font-normal">
                    <CalendarIcon size={14} className="mr-2" />
                    {placementDate ? format(placementDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={placementDate} onSelect={setPlacementDate} />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Preferred Origin Country</Label>
              <Select value={form.originCountry} onValueChange={set("originCountry")}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {countriesList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Destination Port</Label>
              <Input className="mt-1.5" value={form.destPort} onChange={set("destPort")} placeholder="e.g. Chittagong" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Destination Country</Label>
              <Select value={form.destCountry} onValueChange={set("destCountry")}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {countriesList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Shipping Terms / Incoterms</Label>
              <Select value={form.incoterms} onValueChange={set("incoterms")}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="CFR / CIF / FOB" /></SelectTrigger>
                <SelectContent>
                  {incotermsOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Preferred Payment Terms</Label>
              <Select value={form.paymentTerms} onValueChange={set("paymentTerms")}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select payment terms" /></SelectTrigger>
                <SelectContent>
                  {paymentTermsOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {simple && (
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Message</Label>
            <Textarea rows={4} className="mt-1.5" value={form.message} onChange={set("message")} placeholder="Tell us about your requirements..." />
          </div>
        )}
      </div>
      <Button type="submit" disabled={submitting} className="mt-6 w-full md:w-auto text-white rounded-md h-11 px-7" style={{ backgroundColor: "var(--brand-700)" }}>
        {submitting ? "Submitting..." : (<span className="inline-flex items-center gap-2">Send Inquiry <Send size={15} /></span>)}
      </Button>
    </form>
  );
}
